import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { STORAGE_KEYS } from '../../constants';
import type { User, LoginResponse } from '../../types';

export interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken?: string | null;
  isAuthenticated?: boolean;
  isLoading?: boolean;
}

// Safe accessors
const getTokenFromStorage = (): string | null => {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    return token && token !== 'undefined' && token !== 'null' ? token : null;
  } catch {
    return null;
  }
};

const getRefreshTokenFromStorage = (): string | null => {
  try {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    return refreshToken && refreshToken !== 'undefined' && refreshToken !== 'null' ? refreshToken : null;
  } catch {
    return null;
  }
};

const getUserFromStorage = (): User | null => {
  try {
    const userStr = localStorage.getItem(STORAGE_KEYS.USER);
    if (!userStr || userStr === 'undefined' || userStr === 'null') return null;
    return JSON.parse(userStr);
  } catch (e) {
    return null;
  }
};

const loadAuthState = (): AuthState => {
  const token = getTokenFromStorage();
  const refreshToken = getRefreshTokenFromStorage();
  const user = getUserFromStorage();

  return {
    user,
    token,
    refreshToken,
    isAuthenticated: !!(token && user),
    isLoading: false
  };
};

const initialState: AuthState = loadAuthState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // used by login flow
    loginSuccess: (state, action: PayloadAction<LoginResponse>) => {
      const { accessToken, refreshToken, user } = action.payload as any;
      state.user = user;
      state.token = accessToken;
      state.refreshToken = refreshToken;
      state.isAuthenticated = true;
      state.isLoading = false;

      try { localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken); } catch {}
      try { localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken || ''); } catch {}
      try { localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user)); } catch {}
    },

    loginFailure: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.isLoading = false;

      try { localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN); } catch {}
      try { localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN); } catch {}
      try { localStorage.removeItem(STORAGE_KEYS.USER); } catch {}
    },

    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.isLoading = false;

      try { localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN); } catch {}
      try { localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN); } catch {}
      try { localStorage.removeItem(STORAGE_KEYS.USER); } catch {}
    },

    updateUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      try { localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(action.payload)); } catch {}
    },

    updateToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      try { localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, action.payload); } catch {}
    },

    // backwards-compatible simple setters
    setCredentials: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      try { localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, action.payload.token); } catch {}
      try { localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(action.payload.user)); } catch {}
    },

    clearCredentials: (state) => {
      state.user = null;
      state.token = null;
      try { localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN); } catch {}
      try { localStorage.removeItem(STORAGE_KEYS.USER); } catch {}
    },

    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
      try { localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(action.payload)); } catch {}
    }
  }
});

export const {
  setLoading,
  loginSuccess,
  loginFailure,
  logout,
  updateUser,
  updateToken,
  setCredentials,
  clearCredentials,
  setUser
} = authSlice.actions;

export default authSlice.reducer;

// Selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth;
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => !!state.auth.isAuthenticated;
export const selectIsLoading = (state: { auth: AuthState }) => !!state.auth.isLoading; 